// src/hooks/useOrgOperations.js
//
// Pass 49B §4 — the realtime operations data source for the Bookings & Operations workspace.
// Pass 87.2 — now PROVIDER-AWARE: when an OperationsDataProvider is mounted above (the normal
// OrganizationDashboardShell path), this hook returns the ONE shared, org-scoped, season-scoped
// session instead of opening its own listeners — so Overview → Bookings navigation reuses data
// already loaded and never re-fetches the organisation dataset. It also understands the SELECTED
// season: the current season uses the fast scoped realtime feed; a historical season transparently
// escalates to the lazily-loaded (read-only) all-seasons snapshot. With no provider (isolated
// render/legacy tests) it falls back to the original self-contained listeners.

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeBooking } from '../utils/booking';
import { normalizeBookingCase } from '../utils/bookingCase';
import { PLATFORM_COLLECTIONS } from '../utils/platformSchema';
import { detectOperationalConflicts } from '../utils/operationsWorkspace';
import { useOptionalOperationsData } from './useOperationsData';

function msOf(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

// ── Standalone engine (no provider): the original self-contained listeners. Effectively used only
// by isolated render paths; the shell always mounts the provider. Kept unscoped so those paths and
// any direct consumer keep the historical "all org bookings" semantics.
function useStandaloneOrgOperations(organizationId) {
  const [rawBookings, setRawBookings] = useState([]);
  const [members, setMembers] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [sourceIntakes, setSourceIntakes] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    if (!organizationId) { setRawBookings([]); setBookingsLoading(false); return undefined; }
    setBookingsLoading(true);
    const q = query(collection(db, 'bookings'), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(
      q,
      (snap) => { setRawBookings(snap.docs.map((d) => normalizeBooking({ id: d.id, ...d.data() }))); setLastUpdatedAt(new Date()); setBookingsLoading(false); },
      (err) => { console.error('useOrgOperations bookings listener error:', err); setError(err); setBookingsLoading(false); },
    );
    return () => unsub();
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) { setMembers([]); setMembersLoading(false); return undefined; }
    setMembersLoading(true);
    const q = query(collection(db, 'organizationMembers'), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(
      q,
      (snap) => { setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setMembersLoading(false); },
      (err) => { console.error('useOrgOperations members listener error:', err); setError(err); setMembersLoading(false); },
    );
    return () => unsub();
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) { setBookingRequests([]); return undefined; }
    const q = query(collection(db, PLATFORM_COLLECTIONS.BOOKING_REQUESTS), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(q, (snap) => setBookingRequests(snap.docs.map((d) => normalizeBookingCase({ id: d.id, ...d.data() }))), (err) => console.error('useOrgOperations requests listener error:', err));
    return () => unsub();
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) { setSourceIntakes([]); return undefined; }
    const q = query(collection(db, PLATFORM_COLLECTIONS.SOURCE_INTAKE), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(q, (snap) => setSourceIntakes(snap.docs.map((d) => ({ intakeId: d.id, ...d.data() }))), (err) => console.error('useOrgOperations intake listener error:', err));
    return () => unsub();
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) { setAvailabilitySlots([]); return undefined; }
    const q = query(collection(db, PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(q, (snap) => setAvailabilitySlots(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => console.error('useOrgOperations availability listener error:', err));
    return () => unsub();
  }, [organizationId]);

  const bookings = useMemo(() => [...rawBookings].sort((a, b) => (msOf(a.startAt) || 0) - (msOf(b.startAt) || 0)), [rawBookings]);
  const conflicts = useMemo(() => detectOperationalConflicts(bookings), [bookings]);

  return { loading: bookingsLoading || membersLoading, error, lastUpdatedAt, bookings, members, bookingRequests, sourceIntakes, availabilitySlots, conflicts };
}

/**
 * @param organizationId  the organisation whose operations to read.
 * @param opts.seasonId          the currently-selected season (optional).
 * @param opts.currentSeasonId   the organisation's current season (optional).
 *   When a historical season is selected under a provider, the hook escalates to the lazily-loaded
 *   all-seasons snapshot so archived seasons stay fully reachable (read-only).
 */
export function useOrgOperations(organizationId, { seasonId = '', currentSeasonId = '' } = {}) {
  const ctx = useOptionalOperationsData();
  const providerActive = !!(ctx && ctx.orgId === organizationId && organizationId);
  // Satisfy the rules of hooks: always call the standalone engine, but disabled (no org) whenever a
  // provider is active, so it opens no listeners in the normal shell path.
  const standalone = useStandaloneOrgOperations(providerActive ? '' : organizationId);

  const resolvedCurrent = currentSeasonId || ctx?.currentSeasonId || '';
  const needHistorical = !!(providerActive && seasonId && resolvedCurrent && seasonId !== resolvedCurrent);

  // The Bookings workspace needs the auxiliary datasets (availability/intake) and, for a historical
  // season, the all-seasons snapshot. Request them from the shared session exactly when needed.
  useEffect(() => {
    if (!providerActive) return;
    ctx.requestAux();
    if (needHistorical) ctx.requestAllBookings();
  }, [providerActive, needHistorical, ctx]);

  return useMemo(() => {
    if (!providerActive) return standalone;
    const bookings = needHistorical ? (ctx.allBookings || []) : ctx.bookings;
    const loading = needHistorical ? (ctx.allBookings == null || ctx.allBookingsLoading) : ctx.bookingsLoading;
    return {
      loading,
      error: ctx.error,
      lastUpdatedAt: ctx.lastUpdatedAt,
      fromCache: ctx.fromCache,
      bookings,
      members: ctx.members,
      bookingRequests: ctx.bookingRequests,
      sourceIntakes: ctx.sourceIntakes,
      availabilitySlots: ctx.availabilitySlots,
      conflicts: needHistorical ? detectOperationalConflicts(bookings) : ctx.conflicts,
    };
  }, [providerActive, needHistorical, standalone, ctx]);
}

export default useOrgOperations;
